/**
 * Scene Manager supporting multi-scene lessons, transitions, layering, and lifecycle.
 */
import { Stage } from './Stage';
import { Timeline } from './Timeline';
import { Camera } from './Camera';
import { SVGObject } from '../objects/SVGObject';

export type SceneTransitionType = 'none' | 'fade' | 'slideLeft' | 'slideRight' | 'zoomIn' | 'zoomOut' | 'iris' | 'curtain';

export interface SceneConfig {
  id: string;
  title: string;
  topic?: string;
  duration: number; // in seconds
  background?: string;
  transitionIn?: {
    type: SceneTransitionType;
    duration: number;
  };
  transitionOut?: {
    type: SceneTransitionType;
    duration: number;
  };
}

export class Scene {
  public id: string;
  public title: string;
  public topic?: string;
  public duration: number;
  public background: string;
  public transitionIn: { type: SceneTransitionType; duration: number };
  public transitionOut: { type: SceneTransitionType; duration: number };

  public timeline: Timeline;
  private objects: Map<string, SVGObject> = new Map();
  private isMounted: boolean = false;

  constructor(config: SceneConfig) {
    this.id = config.id;
    this.title = config.title;
    this.topic = config.topic;
    this.duration = Math.max(0.1, config.duration);
    this.background = config.background || '#0f172a';
    this.transitionIn = config.transitionIn || { type: 'none', duration: 0.5 };
    this.transitionOut = config.transitionOut || { type: 'none', duration: 0.5 };

    this.timeline = new Timeline(this.duration);
  }

  public onSelectObject?: (id: string) => void;
  public onChangeObject?: (data: { id: string; x: number; y: number; isDragging?: boolean }) => void;

  public addObject(obj: SVGObject): void {
    this.objects.set(obj.id, obj);
    obj.onSelect = (id) => {
      this.onSelectObject?.(id);
    };
    obj.onChange = (data) => {
      this.onChangeObject?.(data);
    };
  }

  public removeObject(id: string): void {
    const obj = this.objects.get(id);
    if (obj) {
      obj.destroy();
      this.objects.delete(id);
    }
  }

  public getObject<T extends SVGObject = SVGObject>(id: string): T | undefined {
    return this.objects.get(id) as T;
  }

  public getAllObjects(): SVGObject[] {
    return Array.from(this.objects.values());
  }

  public mount(stage: Stage): void {
    stage.setBackground(this.background);
    for (const obj of this.objects.values()) {
      obj.mount(stage);
    }
    this.isMounted = true;
  }

  public unmount(): void {
    for (const obj of this.objects.values()) {
      obj.unmount();
    }
    this.timeline.pause();
    this.isMounted = false;
  }

  public update(time: number, delta: number): void {
    if (!this.isMounted) return;
    this.timeline.evaluate(time, delta);
    for (const obj of this.objects.values()) {
      obj.update(time, delta);
    }
  }

  public clear(): void {
    this.unmount();
    this.objects.clear();
    this.timeline.clear();
  }
}
